import { Box, Button, Dialog, DialogContent, DialogTitle, FormGroup, FormHelperText, MenuItem, Select, TextField, Typography } from "@mui/material";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { ApiResponseType } from "../../types";
import { OptionsStore } from "../common/OptionsLoader";

type Props<T> = {
    open: boolean,
    onClose: () => void,
    onSubmit: (data: T) => Promise<ApiResponseType>,
    title: string,
    subTitle: string,
    fieldLabel: string,
    confirmButtonLabel: string,
    record: T,
    showTemplateType?: boolean
}

const NameDialogWithTemplate = <T extends { name: string, templateTypeId: string }>({ open, onClose, onSubmit, title, subTitle, fieldLabel, confirmButtonLabel, record, showTemplateType = false }: Props<T>) => {
    const templateTypes = OptionsStore.useState(s => s.templateTypes);

    const {
        control,
        setValue,
        handleSubmit,
        setError,
        formState: { errors }
    } = useForm({ defaultValues: { name: '', templateTypeId: '' } })

    useEffect(() => {
        setValue('name', record.name);
        setValue('templateTypeId', record.templateTypeId);
    }, [record])

    const onSaveRecord = async (data: T) => {
        const response = await onSubmit({ ...record, ...data });

        if (response.succeeded) {
            setValue('name', '');
            setValue('templateTypeId', '');
            onClose();
        } else {
            setError('name', { message: response.errors[0].description });
        }
    }

    return (
        <Dialog fullWidth maxWidth='sm' onClose={onClose} open={open}>
            <DialogTitle sx={{ pt: 12, mx: 'auto', textAlign: 'center' }}>
                <Typography variant='h4' component='span' sx={{ mb: 2 }}>
                    {title}
                </Typography>
                <Typography variant='body2'>{subTitle}</Typography>
            </DialogTitle>
            <DialogContent sx={{ pb: 12, mx: 'auto' }}>
                <Box component='form' sx={{ mt: 4 }} onSubmit={handleSubmit(onSaveRecord)}>
                    <FormGroup sx={{ mb: 1 }}>
                        <Controller
                            name='name'
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { value, onChange } }) => (
                                <TextField
                                    size="small"
                                    fullWidth
                                    value={value}
                                    label={fieldLabel}
                                    onChange={onChange}
                                    error={Boolean(errors.name)}
                                    placeholder={`Enter ${fieldLabel}`}
                                />
                            )}
                        />
                        {errors.name && (
                            <FormHelperText sx={{ color: 'error.main' }}>{errors.name.message ? errors.name.message : <>Please enter a valid {fieldLabel.toLowerCase()}</>}</FormHelperText>
                        )}
                    </FormGroup>

                    <FormGroup sx={{ mb: 1 }}>
                        <Controller
                            name='templateTypeId'
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { value, onChange } }) => (
                                <Select fullWidth
                                    size="small"
                                    value={value}
                                    label={fieldLabel}
                                    onChange={onChange}
                                    error={Boolean(errors.templateTypeId)}
                                    placeholder={`Enter ${fieldLabel}`}>
                                    <MenuItem></MenuItem>
                                    {templateTypes.map(s => <MenuItem key={s.value} value={s.value}>{s.name}</MenuItem>)}
                                </Select>
                            )}
                        />
                        {errors.name && (
                            <FormHelperText sx={{ color: 'error.main' }}>{errors.templateTypeId?.message ? errors.templateTypeId.message : <>Please enter a valid teemplate type</>}</FormHelperText>
                        )}
                    </FormGroup>

                    <Box className='demo-space-x' sx={{ '&>:last-child': { mr: 0 } }}>
                        <Button size='small' type='submit' variant='contained'>
                            {confirmButtonLabel}
                        </Button>
                        <Button size='small' variant='outlined' color='secondary' onClick={onClose}>
                            Cancel
                        </Button>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>

    )
}

export { NameDialogWithTemplate }
export default NameDialogWithTemplate;